import React, { useState, useEffect } from 'react';
import './CosmicNotification.css';

const CosmicNotification = ({ 
  isVisible, 
  onClose, 
  type = 'success', 
  title, 
  message, 
  duration = 4000,
  icon 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      
      // Auto-close after duration
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Wait for animation to complete
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: icon || '🎉',
          gradient: 'linear-gradient(135deg, #4ade80, #22c55e)',
          borderColor: 'rgba(74, 222, 128, 0.5)',
          glowColor: 'rgba(74, 222, 128, 0.3)'
        };
      case 'error':
        return {
          icon: icon || '❌',
          gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
          borderColor: 'rgba(239, 68, 68, 0.5)',
          glowColor: 'rgba(239, 68, 68, 0.3)'
        };
      case 'warning':
        return {
          icon: icon || '⚠️',
          gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
          borderColor: 'rgba(245, 158, 11, 0.5)',
          glowColor: 'rgba(245, 158, 11, 0.3)'
        };
      case 'info':
        return {
          icon: icon || 'ℹ️',
          gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          borderColor: 'rgba(59, 130, 246, 0.5)',
          glowColor: 'rgba(59, 130, 246, 0.3)'
        };
      case 'cosmic':
        return {
          icon: icon || '🌌',
          gradient: 'linear-gradient(135deg, #7877c6, #ff77c6)',
          borderColor: 'rgba(120, 119, 198, 0.5)',
          glowColor: 'rgba(120, 119, 198, 0.3)'
        };
      default:
        return {
          icon: icon || '✨',
          gradient: 'linear-gradient(135deg, #7877c6, #ff77c6)',
          borderColor: 'rgba(120, 119, 198, 0.5)',
          glowColor: 'rgba(120, 119, 198, 0.3)'
        };
    }
  };

  if (!isVisible) return null;

  const typeStyles = getTypeStyles();

  return (
    <div className={`cosmic-notification-overlay ${isAnimating ? 'visible' : ''}`}>
      <div 
        className={`cosmic-notification ${type} ${isAnimating ? 'animate-in' : 'animate-out'}`}
        style={{
          background: `${typeStyles.gradient}, rgba(15, 15, 35, 0.95)`,
          borderColor: typeStyles.borderColor,
          boxShadow: `0 0 30px ${typeStyles.glowColor}, 0 10px 40px rgba(0, 0, 0, 0.3)`
        }}
      >
        {/* Close Button */}
        <button className="cosmic-notification-close" onClick={handleClose}>
          ×
        </button>

        {/* Icon */}
        <div className="cosmic-notification-icon">
          {typeStyles.icon}
        </div>

        {/* Content */}
        <div className="cosmic-notification-content">
          {title && <h3 className="cosmic-notification-title">{title}</h3>}
          <p className="cosmic-notification-message">{message}</p>
        </div>

        {/* Progress Bar */}
        <div className="cosmic-notification-progress">
          <div 
            className="cosmic-notification-progress-bar"
            style={{
              background: typeStyles.gradient,
              animationDuration: `${duration}ms`
            }}
          />
        </div>

        {/* Particle Effects */}
        <div className="cosmic-particles">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className="cosmic-particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CosmicNotification;
